import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaApple,
  FaGooglePlay,
} from "react-icons/fa";
import { FiCheck, FiLoader } from "react-icons/fi";
import { getFunctions, httpsCallable } from "firebase/functions";
import Logo from "../../assets/Image/Effitrack.png";

const getQuickLinks = (t) => [
  { name: t("footer.links.pricing"), href: "/pricing" },
  { name: t("footer.links.customers"), href: "/admin/customers" },
  { name: t("footer.links.tasks"), href: "/admin/tasks" },
  { name: t("footer.links.support"), href: "mailto:effitrack@effi-track.com" },
];

const getResources = (t) => [
  { name: t("footer.links.privacyPolicy"), href: "/privacy-policy" },
  { name: t("footer.links.termsOfService"), href: "/terms-of-service" },
  { name: t("footer.links.support"), href: "/support" },
];

const socialLinks = [
  { name: "Facebook", href: "https://facebook.com", Icon: FaFacebookF },
  { name: "Twitter", href: "https://twitter.com", Icon: FaTwitter },
  { name: "Instagram", href: "https://instagram.com", Icon: FaInstagram },
  { name: "LinkedIn", href: "https://linkedin.com", Icon: FaLinkedinIn },
];

export default function Footer() {
  const { t } = useTranslation("public");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage(t("footer.newsletter.invalidEmail"));
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const functions = getFunctions();
      const subscribeNewsletter = httpsCallable(functions, "subscribeNewsletter");
      const result = await subscribeNewsletter({ email, source: "footer" });

      setStatus("success");
      setMessage(result.data.message || t("footer.newsletter.thanks"));
      setEmail("");

      // Reset after 5 seconds
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setStatus("error");
      setMessage(error.message || t("footer.newsletter.error"));

      // Reset after 5 seconds
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-900">
      <div className="relative isolate overflow-hidden bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-600 px-6 py-12 text-white sm:px-10 shadow-inner">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-white/20 to-transparent opacity-50 blur-3xl" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
              {t("footer.cta.badge")}
            </p>
            <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">
              {t("footer.cta.title")}
            </h2>
            <p className="max-w-2xl text-sm text-white/90">
              {t("footer.cta.description")}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="mailto:support@effitrack.com"
              className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white px-6 py-3 text-sm font-semibold text-emerald-700 shadow transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              {t("footer.cta.talkToSales")}
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10 hover:-translate-y-0.5"
            >
              {t("footer.cta.startTrial")}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 sm:grid-cols-[1.4fr,1fr,1fr] sm:px-10">
        <div>
          <div className="flex items-center gap-3">
            <img
              src={Logo}
              alt="EffiTrack logo"
              className="h-12 w-12 rounded-2xl border border-emerald-200 bg-white p-1 shadow-sm"
            />
            <div>
              <p className="text-lg font-semibold text-slate-900">EffiTrack</p>
              <p className="text-xs tracking-[0.35em] text-slate-500 uppercase">
                {t("footer.brand.tagline")}
              </p>
            </div>
          </div>
          <p className="mt-6 max-w-sm text-sm text-slate-600">
            {t("footer.brand.description")}
          </p>
          <div className="mt-6 flex items-center gap-4">
            {socialLinks.map(({ name, href, Icon }) => (
              <a
                key={name}
                href={href}
                aria-label={name}
                className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition hover:border-emerald-300 hover:-translate-y-0.5"
              >
                <Icon className="h-4 w-4 text-slate-600 transition group-hover:text-emerald-600" />
              </a>
            ))}
          </div>

          <div className="mt-8">
            <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">
              {t("footer.download.title")}
            </h3>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://apps.apple.com/us/app/effi-track/id6755919441"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-900 px-4 py-2.5 text-white shadow-sm transition hover:bg-slate-800 hover:-translate-y-0.5"
              >
                <FaApple className="h-6 w-6" />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("footer.download.downloadOn")}</p>
                  <p className="text-sm font-semibold">{t("footer.download.appStore")}</p>
                </div>
              </a>
              <div
                className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-700 px-4 py-2.5 text-white/60 shadow-sm cursor-default"
              >
                <FaGooglePlay className="h-5 w-5" />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("footer.download.comingSoon", "Coming Soon")}</p>
                  <p className="text-sm font-semibold">{t("footer.download.googlePlay")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">
            {t("footer.navigation")}
          </h3>
          <ul className="mt-5 space-y-3 text-sm text-slate-700">
            {getQuickLinks(t).map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  className="inline-flex items-center gap-2 rounded-lg px-2 py-1 transition hover:text-emerald-700 hover:bg-emerald-50"
                >
                  <span>{link.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">
            {t("footer.resources")}
          </h3>
          <ul className="mt-5 space-y-3 text-sm text-slate-700">
            {getResources(t).map((link) => (
              <li key={link.name}>
                <Link
                  to={link.href}
                  className="inline-flex items-center gap-2 rounded-lg px-2 py-1 transition hover:text-emerald-700 hover:bg-emerald-50"
                >
                  <span>{link.name}</span>
                </Link>
              </li>
            ))}
          </ul>

          <h3 className="mt-8 text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">
            {t("footer.newsletter.title")}
          </h3>

          {status === "success" ? (
            <div className="mt-4 flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">
              <FiCheck className="h-5 w-5" />
              <span>{message}</span>
            </div>
          ) : (
            <form
              onSubmit={handleSubscribe}
              className={`mt-4 flex rounded-full border bg-white text-sm shadow-sm focus-within:ring-1 ${
                status === "error"
                  ? "border-red-300 focus-within:border-red-400 focus-within:ring-red-400"
                  : "border-slate-200 focus-within:border-emerald-400 focus-within:ring-emerald-400"
              }`}
            >
              <label htmlFor="footer-email" className="sr-only">
                Email address
              </label>
              <input
                id="footer-email"
                type="email"
                aria-label="Email address"
                placeholder={t("footer.newsletter.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "loading"}
                className="flex-1 rounded-l-full bg-transparent px-4 py-2.5 text-slate-900 placeholder:text-slate-500 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="rounded-r-full bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 px-4 py-2.5 font-semibold text-white transition hover:shadow-md disabled:opacity-70 flex items-center gap-2"
              >
                {status === "loading" ? (
                  <>
                    <FiLoader className="h-4 w-4 animate-spin" />
                    <span>{t("footer.newsletter.joining")}</span>
                  </>
                ) : (
                  t("footer.newsletter.join")
                )}
              </button>
            </form>
          )}

          {status === "error" && message && (
            <p className="mt-2 text-xs text-red-600">{message}</p>
          )}

          {status !== "error" && (
            <p className="mt-2 text-xs text-slate-500">
              {t("footer.newsletter.emailsPerMonth")}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white/60">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-6 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between sm:px-10">
          <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-emerald-700">{t("footer.links.privacyPolicy")}</Link>
             <Link to="/terms-of-service" className="hover:text-emerald-700">{t("footer.links.termsOfService")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
